import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link, Redirect, withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withNamespaces } from 'react-i18next';
import { Field, Form, Formik } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Yup from 'yup';
import { detect } from 'detect-browser';
import { capitalize } from 'lodash';
import { authMfa, activateMfa, resetMfa } from '../../redux/actions/AuthActions';
import { isLoggedIn, isMFAValid } from '../../redux/reducers/AuthReducer';
import { isLoading } from '../../redux/reducers/GlobalReducer';
import { Routes as configRouter } from '../../config/Routes';
import HomeLayout from '../../layouts/LoginLayout';
import LoginWelcome from '../../components/Simple/LoginWelcome';
import { Checkbox, Input } from '../../components/common';
import { getOS } from '../../utils/Functions';
import { getMfaButtonClasses } from '../../utils/themeAndStyleHelpers';

class AuthMFA extends Component {
  state = {
    type: '',
    service: null,
  };

  componentDidMount() {
    const {
      history,
      authMfa,
      activateMfa,
      resetMfa,
      route: {
        location: { state: { url } = { url: '' } },
      },
    } = this.props;
    let type = '';
    let service = null;
    if (url === configRouter.SIGN_IN) {
      type = 'activation';
      service = activateMfa;
    } else if (url === configRouter.VALIDATE_TOKEN) {
      type = 'reset';
      service = resetMfa;
    } else {
      type = 'login';
      service = authMfa;
    }

    if (!url) {
      history.push(configRouter.HOME);
      return;
    }

    this.setState({
      type,
      service,
    });
  }

  handleSubmit = (values, actions) => {
    const { service } = this.state;

    const data = {
      mfaCode: values.mfaCode,
    };

    if (values.rememberMFA) {
      data.rememberMFA = values.rememberMFA;
      data.mfaName = values.mfaName;
    }

    service(data).then(() => {
      actions.setSubmitting(false);
    });
  };

  initialValues = () => {
    const browser = detect();
    const computerName = `${capitalize(browser.name)} - ${getOS()}`;
    return {
      mfaCode: '',
      rememberMFA: false,
      mfaName: computerName,
    };
  };

  renderRedirect = () => {
    const { type } = this.state;
    const {
      isMFAValid,
      route: {
        location: { pathname },
      },
    } = this.props;
    if (isMFAValid) {
      if (type === 'activation' || type === 'reset') {
        return (
          <Redirect
            to={{
              pathname: configRouter.LOGIN,
              state: { url: pathname },
            }}
          />
        );
      }
    }
    return null;
  };

  validationSchema = () => {
    const { t } = this.props;
    return Yup.object().shape({
      mfaCode: Yup.string()
        .matches(/^[0-9]*$/, t('validationOnlyNumbers'))
        .required(t('validationRequired')),
      mfaName: Yup.string().when('rememberMFA', {
        is: true,
        then: Yup.string().required(t('validationRequired')),
        otherwise: Yup.string(),
      }),
    });
  };

  render() {
    const { type } = this.state;
    const { t, route, isLoading } = this.props;
    const mfaButtonClasses = getMfaButtonClasses();

    return (
      <HomeLayout>
        {this.renderRedirect()}
        <LoginWelcome title={t('authMfaTitle')} subtitle={t('authMfaSubtitle')} />
        <Formik
          initialValues={this.initialValues()}
          onSubmit={(values, actions) => {
            this.handleSubmit(values, actions);
          }}
          validationSchema={this.validationSchema()}
        >
          {(props) => {
            const { values } = props;
            return (
              <Form className="px-xl-5">
                <Field component={Input} label={t('labelAuthCode')} name="mfaCode" autoComplete="off" placeholder={t('placeholderAuthCode')} required />
                {type === 'login' && (
                  <div>
                    <Field component={Checkbox} name="rememberMFA" id="rememberMFA" label={t('labelRememberMfa')} />
                    {values.rememberMFA && <Field component={Input} label={t('labelMfaName')} name="mfaName" autoComplete="off" placeholder={t('placeholderMfaName')} />}
                  </div>
                )}
                <button type="submit" className={`${mfaButtonClasses}`} disabled={props.isSubmitting}>
                  {t('buttonConfirm')}
                  {isLoading && <FontAwesomeIcon icon="spinner" size="lg" spin className="mx-2" />}
                </button>
                {type !== 'login' && (
                  <Link to={{ pathname: configRouter.AUTH_QR, state: { from: route.location.pathname } }} className="form-control btn btn-ghost mt-3 text-uppercase">
                    {t('buttonScanAgain')}
                  </Link>
                )}
              </Form>
            );
          }}
        </Formik>
        <div className="text-center mt-4">
          <small className="text-small">
            {t('authMfaFooterText')}
            <a href="mailto:integration@d24.com" className="mx-2 classic-link">
              {t('authMfaFooterLink')}
            </a>
          </small>
        </div>
        {type === 'login' && (
          <div className="text-center">
            <small className="text-small">
              {t('labelGoBackTo')}
              <Link to={configRouter.LOGIN} className="mx-2 classic-link">
                {t('loginName')}
              </Link>
            </small>
          </div>
        )}
      </HomeLayout>
    );
  }
}

AuthMFA.propTypes = {
  authMfa: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  isLoading: isLoading(state),
  isMFAValid: isMFAValid(state),
  isLoggedIn: isLoggedIn(state),
});
const mapDispatchToProps = {
  activateMfa,
  authMfa,
  resetMfa,
};

export default compose(withRouter, withNamespaces(), connect(mapStateToProps, mapDispatchToProps))(AuthMFA);
